import React, { useEffect, useCallback, useRef, useState } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import proptypes from 'prop-types';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { UserLists } from '@thd-olt-component-react/user-lists';
import { useNavigate, useLocation } from '@thd-olt-component-react/router';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import { ListBenefits } from '@thd-olt-component-react/list-benefits';
import { CustomerAccountNavigation, useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Typography } from '@one-thd/sui-atomic-components';
import { LISTS_CRUMB } from '../../constants';
import { updateQueryParams } from '../../helpers';
import { usePageTitle } from '../../hooks/usePageTitle';

const { bool } = proptypes;

export const Summary = ({ authenticated }) => {
  declareContexts([ExperienceContext, CustomerContext]);
  usePageTitle('List Summary Page');
  const navigate = useNavigate();
  const [isClient, setIsClient] = useState(false);
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;
  const scrollToRef = useRef();

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('list-summary.page-load');
    setIsClient(true);
  }, []);

  const urlParams = new URLSearchParams(querystring);
  const page = Number(urlParams.get('page')) || 1;
  const onPageChange = useCallback((pageNo) => {
    const updatePath = updateQueryParams(path, [
      {
        paramName: 'page',
        paramValue: pageNo
      }
    ]);
    navigate(updatePath);

    return scrollToRef?.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
  });

  const summaryCrumbs = useAccountCrumbs([
    LISTS_CRUMB
  ]).map((crumb) => {
    return {
      label: crumb.label,
      url: crumb.path || crumb.url,
    };
  });

  const listSummaryWrapperClass = classNames({
    'sui-col-span-12 lg:sui-col-span-9 sui-pr-4': authenticated || !isClient,
    'sui-col-span-12 sui-px-4': (!authenticated && isClient)
  });

  return (
    <div className="sui-max-w-screen-2xl sui-mx-auto">
      <div className="sui-pl-4 sui-pt-4">
        <ErrorBoundary name="list-summary-breadcrumbs">
          <Breadcrumbs staticCrumbs={summaryCrumbs} />
        </ErrorBoundary>
        <Typography variant="h1" weight="display">
          LISTS
        </Typography>
      </div>
      <div className="sui-grid sui-grid-cols-12">
        <div className="lg:sui-col-span-3 sui-col-span-12 lg:sui-pl-3 lg:sui-pt-8">
          <ErrorBoundary name="list-summary-left-hand-nav">
            {
              authenticated && (
                <CustomerAccountNavigation />
              )
            }
          </ErrorBoundary>
        </div>
        <div className={listSummaryWrapperClass}>
          {
            isClient && authenticated && (
              <ErrorBoundary name="list-summary-listing">
                <span ref={scrollToRef} />
                <UserLists iconSize="regular" page={page} onPageChange={onPageChange} />
              </ErrorBoundary>
            )
          }
          {
            isClient && !authenticated && (
              <ErrorBoundary name="list-summary-guest">
                <ListBenefits />
              </ErrorBoundary>
            )
          }
        </div>
      </div>
      <ErrorBoundary name="list-summary-listing-back-to-top">
        <BackToTop />
      </ErrorBoundary>
    </div>
  );
};

Summary.displayName = 'MyListSummaryPage';

Summary.dataModel = extend(
  UserLists,
  Breadcrumbs,
  CustomerAccountNavigation
);

Summary.propTypes = {
  authenticated: bool
};

Summary.defaultProps = {
  authenticated: false
};
